/**
 * Centralized role configuration for the application.
 * Single source of truth for role IDs, labels, and routing.
 * Add new roles here when the backend introduces them.
 *
 * Role mapping: Company(Recruiter)=1, Jobseeker=2, User=3, Client=4, Agency=5
 * Role 3 is "User" (e.g. invited team member); role 4 is "Client". They are separate.
 */

import { toast } from "react-toastify";

/** Role IDs returned by the API (e.g. social login, auth) */
export const ROLE_IDS = {
  /** Company / Recruiter (role 1) */
  RECRUITER: 1,
  /** Jobseeker / Applicant (role 2) */
  APPLICANT: 2,
  /** User (role 3) – e.g. invited team member; distinct from Client (4) */
  USER: 3,
  /** Client (role 4) */
  CLIENT: 4,
  /** Agency (role 5) */
  AGENCY: 5,
} as const;

/** Display labels for each role (e.g. dropdowns, user management) */
export const ROLE_LABELS: Record<number, string> = {
  [ROLE_IDS.RECRUITER]: "Company",
  [ROLE_IDS.APPLICANT]: "Jobseeker",
  [ROLE_IDS.USER]: "User",
  [ROLE_IDS.CLIENT]: "Client",
  [ROLE_IDS.AGENCY]: "Agency",
};

/** Default route for each role after login. null = send to /user-role (role selection). */
const ROLE_DEFAULT_ROUTES: Record<number, string> = {
  [ROLE_IDS.RECRUITER]: "/dashboard",
  [ROLE_IDS.APPLICANT]: "/applicant-dashboard",
  [ROLE_IDS.USER]: "/dashboard",
  [ROLE_IDS.CLIENT]: "/client-dashboard",
  [ROLE_IDS.AGENCY]: "/dashboard",
};

/**
 * Coerce API role values to a number. JSON often returns `"3"` instead of `3`, which breaks
 * strict `=== ROLE_IDS.USER` checks and can send incomplete "User" accounts to `/dashboard`.
 */
export function normalizeRoleId(
  roleId: number | string | null | undefined,
): number | undefined {
  if (roleId === null || roleId === undefined) return undefined;
  if (typeof roleId === "string" && roleId.trim() === "") return undefined;
  const n = Number(roleId);
  return Number.isNaN(n) ? undefined : n;
}

/**
 * True when the account is still API role "User" (3) and has not run `/accounts/update-role/`
 * yet. Selection flow sets role to 1 / 2 / 5 — until then, block all dashboards. The only
 * exception is legacy data: role 3 with a non-empty `job_seeker_id` (applicant routing).
 * Do not use `profile_completed` here — the API can set it incorrectly before role changes.
 */
export function isPendingUserRoleSelection(
  role: number | null | undefined,
  jobSeekerId: string | number | null | undefined,
): boolean {
  if (normalizeRoleId(role) !== ROLE_IDS.USER) return false;
  if (jobSeekerId != null && String(jobSeekerId).trim() !== "") return false;
  return true;
}

/**
 * Pull the job identifier out of a job URL or path.
 *
 * The route is /related-jobs/:jobId and :jobId may be EITHER a numeric id
 * (internal navigation) or a slug (shared links, e.g.
 * /related-jobs/arcitech-ai-ui-ux-design-manager). Every call site used to test
 * for digits only, so a candidate arriving on a shared slug link lost their job
 * context: they were treated as having no job, which sent them to the dashboard
 * after upload instead of back to the job they were applying for.
 *
 * Segment-based rather than a regex so both URL shapes and both id formats work,
 * and so "/jobs/related-jobs/x" cannot yield "related-jobs" as the id.
 */
export function extractJobIdFromPath(
  pathOrUrl: string | null | undefined,
): string | null {
  if (!pathOrUrl || typeof pathOrUrl !== "string") return null;
  const clean = pathOrUrl.split("?")[0].split("#")[0];
  const segments = clean.split("/").filter(Boolean);

  const relatedAt = segments.lastIndexOf("related-jobs");
  if (relatedAt !== -1 && segments[relatedAt + 1]) {
    return segments[relatedAt + 1];
  }

  const jobsAt = segments.lastIndexOf("jobs");
  if (
    jobsAt !== -1 &&
    segments[jobsAt + 1] &&
    segments[jobsAt + 1] !== "related-jobs"
  ) {
    return segments[jobsAt + 1];
  }

  return null;
}

/**
 * Paths where a candidate is part-way through an application.
 *
 * A fresh account is API role 3 until /accounts/update-role/ runs, and the
 * jobseeker id only exists once the resume is uploaded - so on these paths
 * isPendingUserRoleSelection() is true for someone who is plainly a jobseeker and
 * has simply not uploaded yet. Redirecting them to /user-role abandons the
 * application mid-flow.
 *
 * Consumed by BOTH RoleBasedRoutes and PrivateRoute. They are separate gates and
 * both fire; when this rule lived inline in each of them, fixing one still left
 * the other redirecting, which is exactly how this bug survived a "fix".
 */
export function isCandidateApplicationPath(pathname: string): boolean {
  if (!pathname) return false;
  return (
    pathname === "/resume-builder" ||
    pathname.startsWith("/jobs/") ||
    pathname.includes("/related-jobs/")
  );
}

/** Route set key used by RoleBasedRoutes to pick RecruiterRoutes, ApplicantRoutes, ClientRoutes, or AgencyRoutes */
export const ROUTE_SET_KEYS = {
  RECRUITER: "recruiter",
  APPLICANT: "applicant",
  CLIENT: "client",
  AGENCY: "agency",
} as const;

export type RouteSetKey = (typeof ROUTE_SET_KEYS)[keyof typeof ROUTE_SET_KEYS];

/**
 * Returns the default redirect route for a given role ID.
 * @param roleId - User role from API
 * @returns Route path or null (use /user-role)
 */
export function getDefaultRouteForRole(
  roleId: number | null | undefined,
): string | null {
  const n = normalizeRoleId(roleId);
  if (n == null) return null;
  return ROLE_DEFAULT_ROUTES[n] ?? null;
}

/**
 * Returns the route set key for RoleBasedRoutes (recruiter | applicant | client).
 * @param roleId - User role from API
 * @returns Route set key or null
 */
export function getRouteSetKeyForRole(
  roleId: number | null | undefined,
): RouteSetKey | null {
  const n = normalizeRoleId(roleId);
  if (n == null) return null;
  switch (n) {
    case ROLE_IDS.RECRUITER:
    case ROLE_IDS.USER:
      return ROUTE_SET_KEYS.RECRUITER;
    case ROLE_IDS.APPLICANT:
      return ROUTE_SET_KEYS.APPLICANT;
    case ROLE_IDS.CLIENT:
      return ROUTE_SET_KEYS.CLIENT;
    case ROLE_IDS.AGENCY:
      return ROUTE_SET_KEYS.AGENCY;
    default:
      return null;
  }
}

/**
 * Route set to actually render for a role, given where the user is standing.
 *
 * Role 3 ("User") has no route tree of its own - getRouteSetKeyForRole maps it to the
 * recruiter set, which contains none of the apply-flow routes. A lead-form candidate is
 * API role 3 from email verification until /accounts/update-role/ runs, and has no
 * jobseeker id until the resume upload, so the OTP flow lands them on /resume-builder
 * while still role 3: RecruiterRoutes has no such route and fell through to PageNotFound
 * (the 404 on /resume-builder after entering the code). resolvePostLoginRedirect and both
 * pending-role-selection guards already let that candidate through - the route set has to
 * agree with them, or they are bounced one render later.
 *
 * Scoped to role 3: every other role keeps getRouteSetKeyForRole's mapping unchanged.
 */
export function getEffectiveRouteSetKey(
  roleId: number | string | null | undefined,
  jobSeekerId: string | number | null | undefined,
  pathname: string | null | undefined,
): RouteSetKey | null {
  const role = normalizeRoleId(roleId);
  if (role === ROLE_IDS.USER) {
    const hasJobSeekerId =
      jobSeekerId != null && String(jobSeekerId).trim() !== "";
    // Jobseeker id present = a jobseeker whose role simply has not been updated yet;
    // apply-flow path = mid-application, before the upload creates that id.
    if (hasJobSeekerId || isCandidateApplicationPath(pathname ?? "")) {
      return ROUTE_SET_KEYS.APPLICANT;
    }
  }
  return getRouteSetKeyForRole(role);
}

/**
 * Whether the role should see the client dashboard (role 4 only; role 3 is User, not Client).
 */
export function isClientRole(roleId: number | null | undefined): boolean {
  return roleId === ROLE_IDS.CLIENT;
}

/**
 * Default redirect path for invited members based on backend groups (e.g. after edit-profile).
 */
export function getDefaultRouteForGroup(
  groups: string[] | null | undefined,
): string {
  if (!groups?.length) return "/dashboard";
  const g = groups.map((s) => s?.toLowerCase?.() ?? "");
  if (g.some((s) => s === "client")) return "/client-dashboard";
  if (g.some((s) => s === "recruiter" || s === "admin" || s === "owner"))
    return "/dashboard";
  return "/dashboard";
}

/**
 * For role 1/5 (recruiter/agency) on login: returns onboarding URL only when the user is an
 * **owner** who hasn't completed profile (so they can complete company/agency setup).
 * Recruiters and admins (non-owners) must NOT be redirected to onboarding on login — they go to dashboard.
 * profile_completed: treat only explicit true as "onboarded"; null/false/undefined → redirect to onboarding.
 * For agency (role 5) returns /onboarding/agency-details; for recruiter/company (role 1) returns /onboarding/company-details.
 */
export function getInvitedMemberOnboardingUrl(
  groups: string[] | null | undefined,
  email: string | null | undefined,
  profileCompleted: boolean | null | undefined,
  role?: number | null,
): string | null {
  if (!groups?.length) return null;
  if (profileCompleted === true) return null; // Only skip when explicitly completed (null/false/undefined = not completed)
  const g = groups.map((s) => s?.toLowerCase?.() ?? "");
  // Only owners get redirected to onboarding on login (to complete company/agency registration)
  const hasOwner = g.some((s) => s === "owner");
  if (!hasOwner) return null; // Recruiter/admin (non-owner) → do not redirect to onboarding
  const params = new URLSearchParams();
  if (email) params.set("email", email);
  const basePath = role === ROLE_IDS.AGENCY ? "/onboarding/agency-details" : "/onboarding/company-details";
  const query = params.toString();
  return query ? `${basePath}?${query}` : basePath;
}

/**
 * Whether the user is an invited member (admin/recruiter group with role 1 or 5).
 * When true AND source=invitation is present, we ALWAYS redirect to onboarding regardless of profile_completed.
 */
export function isInvitedMember(
  role: number | null | undefined,
  groups: string[] | null | undefined,
): boolean {
  if (role !== ROLE_IDS.RECRUITER && role !== ROLE_IDS.AGENCY) return false;
  if (!groups?.length) return false;
  const g = groups.map((s) => s?.toLowerCase?.() ?? "");
  return g.some((s) => s === "admin" || s === "recruiter");
}

/**
 * Whether the user should see owner-only company/agency profile prompts (e.g. "complete profile" dashboard banner).
 * Invited admins/recruiters are excluded; primary accounts (often empty groups before "owner" is assigned) are included.
 */
export function isCompanyAgencyProfileOwner(
  role: number | null | undefined,
  groups: string[] | null | undefined,
): boolean {
  if (role !== ROLE_IDS.RECRUITER && role !== ROLE_IDS.AGENCY) return false;
  const g = (groups ?? []).map((s) => s?.toLowerCase?.() ?? "");
  if (g.includes("owner")) return true;
  if (isInvitedMember(role, groups)) return false;
  return true;
}

/**
 * Owner / primary company-agency accounts that must finish onboarding before /dashboard.
 * Invited non-owner recruiters/admins are excluded (same idea as getInvitedMemberOnboardingUrl).
 * Until `profile_completed` is true (after Continue / submit), URL navigation to /dashboard is blocked.
 *
 * If `companyId` is set, the user is already linked to a company/agency — do not force onboarding
 * even when the API omits or delays `profile_completed` (common for long-lived accounts).
 */
export function requiresCompanyAgencyOnboarding(
  role: number | null | undefined,
  profileCompleted: boolean | null | undefined,
  groups: string[] | null | undefined,
  companyId: number | string | null | undefined,
): boolean {
  const r = normalizeRoleId(role);
  if (r !== ROLE_IDS.RECRUITER && r !== ROLE_IDS.AGENCY) return false;
  if (profileCompleted === true) return false;
  if (companyId != null && String(companyId).trim() !== "") return false;
  return isCompanyAgencyProfileOwner(r, groups);
}

export function getCompanyAgencyOnboardingPath(
  role: number | null | undefined,
): "/onboarding/company-details" | "/onboarding/agency-details" {
  return normalizeRoleId(role) === ROLE_IDS.AGENCY
    ? "/onboarding/agency-details"
    : "/onboarding/company-details";
}

/**
 * Returns the onboarding URL for an invited member (role 1 → company-details, role 5 → agency-details).
 * Does NOT check profile_completed — use when source=invitation is confirmed so onboarding is always shown.
 */
export function getInvitationOnboardingUrl(
  role: number | null | undefined,
  email: string | null | undefined,
): string | null {
  if (role !== ROLE_IDS.RECRUITER && role !== ROLE_IDS.AGENCY) return null;
  const params = new URLSearchParams({ source: "invitation" });
  if (email) params.set("email", email);
  const basePath = role === ROLE_IDS.AGENCY ? "/onboarding/agency-details" : "/onboarding/company-details";
  return `${basePath}?${params.toString()}`;
}

/**
 * Whether the role has a direct dashboard (no need for /user-role selection).
 */
export function hasDirectDashboard(roleId: number | null | undefined): boolean {
  return getDefaultRouteForRole(roleId) != null;
}

/**
 * Minimal user shape needed for post-login redirect decision.
 * Use after social login, email/password login, OTP verification, or loginWithUserId.
 */
export interface PostLoginUser {
  role: number | null | undefined;
  groups?: string[] | null;
  email?: string | null;
  profile_completed?: boolean | null;
  job_seeker_id?: number | string | null;
  /** From API `is_new_user`; not used for role 3 redirect (use `profile_completed` instead). */
  is_new_user?: boolean | null;
  /** When non-empty, user is considered onboarded; redirect to dashboard. Role is determined from groups. */
  full_name?: string | null;
}

/**
 * Resolves the numeric role from a user object that may expose it as the
 * canonical `role` field or the legacy `user_role` alias.
 * Use this everywhere instead of the inline `user?.role ?? user?.user_role` pattern.
 */
export function getUserRole(
  user: { role?: number | null; user_role?: number | null } | null | undefined,
): number | null | undefined {
  const r = user?.role ?? user?.user_role ?? undefined;
  return normalizeRoleId(r);
}

/** True when user has a non-empty full name (already onboarded for name-based flow). */
export function hasFullName(fullName: string | null | undefined): boolean {
  return typeof fullName === "string" && fullName.trim().length > 0;
}

/**
 * Single place for post-login redirect logic. Call after setting auth state (token, user).
 * Handles: invitation link onboarding, role 1/5 onboarding, default role route, client-dashboard, or /user-role.
 *
 * @param user - User with role, groups, email, profile_completed
 * @param redirectUrl - Optional URL the user was trying to reach (e.g. from email link)
 * @param navigate - Router navigate function (e.g. from useNavigate())
 */
export function resolvePostLoginRedirect(
  user: PostLoginUser,
  redirectUrl: string | undefined,
  navigate: ((path: string) => void) | Function,
): void {
  const nav = (path: string) => (navigate as (path: string) => void)(path);
  const role = normalizeRoleId(user.role);
  const isInvitationRedirect =
    redirectUrl &&
    (redirectUrl.includes("/onboarding") || redirectUrl.includes("source=invitation"));

  if (isInvitedMember(role, user.groups) && isInvitationRedirect) {
    toast.success("Logged in successfully");
    if (hasFullName(user.full_name)) {
      nav(getDefaultRouteForRole(role) ?? "/dashboard");
    } else {
      nav(getInvitationOnboardingUrl(role, user.email ?? undefined) ?? "/dashboard");
    }
    return;
  }

  if (role === 1 || role === 5) {
    // Single source of truth: full_name non-empty = onboarded → dashboard. Empty = needs onboarding (name-only or full by groups).
    if (hasFullName(user.full_name)) {
      toast.success("Logged in successfully");
      const defaultRoute = getDefaultRouteForRole(role) ?? "/dashboard";
      const isOnboardingRedirect =
        redirectUrl &&
        (redirectUrl.includes("/onboarding") || redirectUrl.includes("source=invitation"));
      nav(isOnboardingRedirect ? defaultRoute : (redirectUrl ?? defaultRoute));
      return;
    }
    // full_name empty → onboarding. getInvitedMemberOnboardingUrl
    // requires groups to contain "owner", which new signups often don't have yet — so fall back to getInvitationOnboardingUrl.
    let onboardingUrl = getInvitedMemberOnboardingUrl(
      user.groups,
      user.email ?? undefined,
      user.profile_completed,
      role,
    );
    // Existing users already returned above; here only new/unknown. If no URL from groups, use onboarding by role (handles null/undefined is_new_user).
    if (!onboardingUrl) {
      onboardingUrl = getInvitationOnboardingUrl(role, user.email ?? undefined);
    }
    toast.success("Logged in successfully");
    nav(onboardingUrl ?? redirectUrl ?? "/dashboard");
    return;
  }

  // Apply flow: a candidate mid-application has no JobSeeker record yet (it is
  // created by the resume upload), and a fresh account defaults to API role 3
  // until update-role runs. Without this guard they fall into the role-selection
  // branch below and land on /user-role ("Looks like you’re new here") instead
  // of the resume upload, abandoning the application they were part-way through.
  // Placed here so it covers EVERY auth method funnelling through this function
  // - the email verification code and Google sign-in both do.
  //
  // Deliberately narrow: roles 1/5 have already returned above; scoped to
  // applicant (2) / user (3) so client (4) keeps its own destination; and it
  // only fires when the intended destination is a job page, which cannot do
  // anything useful without a resume.
  if (
    (role === ROLE_IDS.APPLICANT || role === ROLE_IDS.USER) &&
    (user.job_seeker_id == null || String(user.job_seeker_id).trim() === "") &&
    redirectUrl
  ) {
    const jobId = extractJobIdFromPath(redirectUrl);
    if (jobId) {
      toast.success("Logged in successfully");
      (navigate as (path: string, opts?: unknown) => void)("/resume-builder", {
        state: { fromCopyLink: true, jobId },
      });
      return;
    }
  }

  // Role 3 (User): must pick company / agency / jobseeker on /user-role until update-role runs.
  // Gate on role id, not profile_completed (API can flag profile before role changes).
  if (isPendingUserRoleSelection(role, user.job_seeker_id)) {
    toast.success("Logged in successfully");
    nav("/user-role");
    return;
  }

  const defaultRoute = getDefaultRouteForRole(role);
  if (defaultRoute) {
    toast.success("Logged in successfully");
    nav(redirectUrl ?? defaultRoute);
    return;
  }
  if (redirectUrl && redirectUrl.includes("/client-dashboard")) {
    toast.success("Logged in successfully");
    nav(redirectUrl);
    return;
  }
  const onboardingUrl = getInvitedMemberOnboardingUrl(
    user.groups,
    user.email ?? undefined,
    user.profile_completed,
    role,
  );
  if (onboardingUrl) {
    toast.success("Logged in successfully");
    nav(onboardingUrl);
  } else {
    nav("/user-role");
  }
}

/**
 * Path prefixes that belong exclusively to recruiter routes (role 1).
 * Shared paths (e.g. /confirm-information, /check-out) are not listed so they
 * are allowed for the role that actually has them in their route set.
 */
const RECRUITER_PATH_PREFIXES = [
  "/dashboard",
  "/view-jobs-data",
  "/view-invited-jobs-data",
  "/view-requested-jobs-data",
  "/view-demo-jobs-data",
  "/meeting",
  "/payment-",
  "/credit-success",
  "/contact-sales",
  "/job-details",
  "/results",
  "/top-up-credit-success",
];

/**
 * Path prefixes that belong exclusively to applicant routes (role 2).
 * Shared paths (confirm-information, check-out, etc.) are not listed.
 */
const APPLICANT_PATH_PREFIXES = [
  "/applicant-dashboard",
  "/resume-builder",
  "/resume-generator",
  "/resume-picker",
  "/resume-customize",
  "/resume-enhancement",
  "/update-applicant-resume",
  "/jobs/",
  "/applicantprofile",
  "/help/troubleshoot-microphone",
  "/ai-practice-interview",
  "/written-assessment",
  "/code-editor",
  "/under-review",
  "/related-jobs",
  "/interview-module",
  "/start-interview",
  "/interview-panel-ready",
  "/practice-assessment",
  "/written-assessment-instruction",
  "/interview-instruction",
  "/ai-interview-guideline",
  "/ai-practice-interview-guideline",
  "/mic-troubleshoot-help",
  "/schedule-confirmation",
];

/** Path prefixes that belong to client routes (role 4) */
const CLIENT_PATH_PREFIXES = ["/client-dashboard"];

/**
 * Paths that are shared between multiple roles (e.g., recruiter and client).
 * These paths return null from getRouteSetForPath, allowing access for any authenticated user.
 * /onboarding is shared so role 1 and 5 can complete company/agency onboarding.
 */
const SHARED_PATH_PREFIXES = ["/view-jobs-data/results", "/onboarding", "/company-details"];

/** Path prefixes that belong to agency routes (role 5). Used when agency has its own dashboard. */
const AGENCY_PATH_PREFIXES = ["/agency-dashboard"];

/**
 * Returns which route set the pathname belongs to (for role-based protection).
 * Used by PrivateRoute to redirect users who hit a path for another role.
 */
export function getRouteSetForPath(pathname: string): RouteSetKey | null {
  if (!pathname || typeof pathname !== "string") return null;
  // Check shared paths first - these are accessible by multiple roles
  if (SHARED_PATH_PREFIXES.some((p) => pathname.startsWith(p))) return null;
  if (CLIENT_PATH_PREFIXES.some((p) => pathname.startsWith(p)))
    return ROUTE_SET_KEYS.CLIENT;
  if (AGENCY_PATH_PREFIXES.some((p) => pathname.startsWith(p)))
    return ROUTE_SET_KEYS.AGENCY;
  if (RECRUITER_PATH_PREFIXES.some((p) => pathname.startsWith(p)))
    return ROUTE_SET_KEYS.RECRUITER;
  if (APPLICANT_PATH_PREFIXES.some((p) => pathname.startsWith(p)))
    return ROUTE_SET_KEYS.APPLICANT;
  return null;
}

/**
 * Returns true if the current path is allowed for the given role.
 * Use in PrivateRoute to redirect when path is not allowed for role.
 * Agency (role 5) is allowed to use recruiter paths (/dashboard, etc.) and gets RecruiterRoutes.
 */
export function isPathAllowedForRole(
  pathname: string,
  roleId: number | null | undefined,
  jobSeekerId?: string | number | null,
): boolean {
  const pathRouteSet = getRouteSetForPath(pathname);
  if (pathRouteSet == null) return true; // common/public or unknown path, allow
  // Effective (not raw) route set, so a role 3 candidate mid-application is not treated
  // as a recruiter on the applicant paths they were just redirected to.
  const userRouteSet = getEffectiveRouteSetKey(roleId, jobSeekerId, pathname);
  if (userRouteSet == null) return true; // no role or unknown role, let other logic handle
  if (pathRouteSet === userRouteSet) return true;
  // Agency uses the same dashboard as recruiter; RoleBasedRoutes renders RecruiterRoutes for agency
  if (userRouteSet === ROUTE_SET_KEYS.AGENCY && pathRouteSet === ROUTE_SET_KEYS.RECRUITER)
    return true;
  return false;
}